import React from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Flex,
  Text,
  Button,
  Stack,
  Link,
  useColorModeValue,
  useDisclosure,
  IconButton,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Avatar,
  Spacer,
} from '@chakra-ui/react';
import { useAuth } from '../../context/AuthContext';
import { auth } from '../../lib/firebase';
import UserSection from './UserSection';
import ColorModeSwitcher from '../ColorModeSwitcher';
import ActivarQuinielas from '../admin/ActivarQuinielas';

const Navbar = () => {
  const { isOpen, onToggle } = useDisclosure();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.email === 'htorres@teiker.mx';
  
  const hoverBg = useColorModeValue('gray.200', 'gray.700');
  const avatarColor = useColorModeValue('white', 'gray.800');

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
  };

  return (
    <Box>
      <Flex
        bg={useColorModeValue('white', 'gray.800')}
        color={useColorModeValue('gray.600', 'white')}
        minH={'60px'}
        py={{ base: 2 }}
        px={{ base: 4 }}
        borderBottom={1}
        borderStyle={'solid'}
        borderColor={useColorModeValue('gray.200', 'gray.900')}
        align={'center'}
      >
        <Flex
          flex={{ base: 1, md: 'auto' }}
          ml={{ base: -2 }}
          display={{ base: 'flex', md: 'none' }}
        >
          <IconButton
            onClick={onToggle}
            icon={
              <Text fontSize="xl">
                {isOpen ? '✕' : '☰'}
              </Text>
            }
            variant={'ghost'}
            aria-label={'Toggle Navigation'}
            p={2}
          />
        </Flex>
        
        <Flex flex={{ base: 1 }} justify={{ base: 'center', md: 'start' }}>
          <Link
            as={RouterLink}
            to="/"
            textAlign={useColorModeValue('left', 'center')}
            fontFamily={'heading'}
            color={useColorModeValue('gray.800', 'white')}
            _hover={{
              textDecoration: 'none',
            }}
          >
            <Text fontSize="xl" fontWeight="bold">
              Quinielas
            </Text>
          </Link>

          <Flex display={{ base: 'none', md: 'flex' }} ml={10}>
            <Stack direction={'row'} spacing={4}>
              {user && (
                <>
                  <Link as={RouterLink} to="/"
                  px={2}
                  py={1}
                  rounded={'md'}
                  _hover={{
                    textDecoration: 'none',
                    bg: hoverBg,
                  }}>
                    Inicio
                  </Link>
                  <Link as={RouterLink} to="/rankings"
                  px={2}
                  py={1}
                  rounded={'md'}
                  _hover={{
                    textDecoration: 'none',
                    bg: hoverBg,
                  }}>
                    Puntuaciones
                  </Link>
                  <Link
                    as={RouterLink}
                    to="/quiniela/quinielas-capturadas"
                    px={2}
                    py={1}
                    rounded={'md'}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Quinielas
                  </Link>
                  {isAdmin && (
                    <Menu>
                      <MenuButton as={Link}
                      px={2}
                      py={1}
                      rounded={'md'}
                      _hover={{
                        textDecoration: 'none',
                        bg: hoverBg,
                      }}>
                        Administración
                      </MenuButton>
                      <MenuList>
                        <MenuItem as={RouterLink} to="/admin/matches">
                          Gestionar Partidos
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/results">
                          Resultados
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/ver-quinielas">
                          Ver Quinielas capturadas
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/activar-quinielas">
                          Activar Quinielas
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/users">
                          Gestionar Usuarios
                        </MenuItem>
                        <MenuItem as={RouterLink} to="/admin/predictions">
                          Gestionar Pronósticos
                        </MenuItem>
                      </MenuList>
                    </Menu>
                  )}
                </>
              )}
            </Stack>
          </Flex>
        </Flex>

        <Stack
          flex={{ base: 1, md: 0 }}
          justify={'flex-end'}
          direction={'row'}
          spacing={6}
        >
          {!loading && (
            <>
              {!user ? (
                <>
                  <Button
                    as={RouterLink}
                    to="/login"
                    fontSize={'sm'}
                    fontWeight={400}
                    variant={'link'}
                  >
                    Iniciar Sesión
                  </Button>
                  <Button
                    as={RouterLink}
                    to="/register"
                    display={{ base: 'none', md: 'inline-flex' }}
                    fontSize={'sm'}
                    fontWeight={600}
                    color={'white'}
                    bg={'blue.400'}
                    _hover={{
                      bg: 'blue.300',
                    }}
                  >
                    Registrarse
                  </Button>
                </>
              ) : (
                <Menu>
                  <MenuButton
                    as={Button}
                    rounded={'full'}
                    variant={'link'}
                    cursor={'pointer'}
                    minW={0}
                  >
                    <HStack>
                      <Avatar
                        size={'sm'}
                        name={user?.name || user?.displayName || user?.email || 'Usuario'}
                        src={user?.photoURL || undefined}
                        bg={user?.photoURL ? undefined : (user?.avatarBgColor || 'blue.500')}
                        color={avatarColor}
                      />
                      <Text display={{ base: 'none', md: 'flex' }}>
                        {user?.name || user?.displayName || user?.email || 'Usuario'}
                      </Text>
                    </HStack>
                  </MenuButton>
                  <MenuList>
                    <MenuItem as={RouterLink} to="/profile">
                      Mi Perfil
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>
                      Cerrar Sesión
                    </MenuItem>
                    <MenuItem closeOnSelect={false}>
                      <ColorModeSwitcher />
                      <Text ml={2}>Modo oscuro</Text>
                    </MenuItem>
                  </MenuList>
                </Menu>
              )}
            </>
          )}
        </Stack>
      </Flex>

      {/* Menú móvil */}
      <Box
        display={{ base: isOpen ? 'block' : 'none', md: 'none' }}
        bg={useColorModeValue('white', 'gray.800')}
        px={4}
        pb={4}
      >
        <Stack as={'nav'} spacing={4}>
          {user && (
            <>
              <Link as={RouterLink} to="/"
                px={2}
                py={1}
                rounded={'md'}
                onClick={onToggle}
                _hover={{
                  textDecoration: 'none',
                  bg: hoverBg,
                }}>
                Inicio
              </Link>
              <Link as={RouterLink} to="/rankings"
                px={2}
                py={1}
                rounded={'md'}
                onClick={onToggle}
                _hover={{
                  textDecoration: 'none',
                  bg: hoverBg,
                }}>
                Puntuaciones
              </Link>
              <Link
                as={RouterLink}
                to="/quiniela/quinielas-capturadas"
                px={2}
                py={1}
                rounded={'md'}
                onClick={onToggle}
                _hover={{
                  textDecoration: 'none',
                  bg: hoverBg,
                }}>
                Quinielas
              </Link>
              {isAdmin && (
                <>
                  <Text px={2} py={1} fontWeight="bold">Administración</Text>
                  <Link as={RouterLink} to="/admin/matches"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Gestionar Partidos
                  </Link>
                  <Link as={RouterLink} to="/admin/results"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Resultados
                  </Link>
                  <Link as={RouterLink} to="/admin/ver-quinielas"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Ver Quinielas capturadas
                  </Link>
                  <Link as={RouterLink} to="/admin/activar-quinielas"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Activar Quinielas
                  </Link>
                  <Link as={RouterLink} to="/admin/users"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Gestionar Usuarios
                  </Link>
                  <Link as={RouterLink} to="/admin/predictions"
                    px={4}
                    py={1}
                    rounded={'md'}
                    onClick={onToggle}
                    _hover={{
                      textDecoration: 'none',
                      bg: hoverBg,
                    }}>
                    Gestionar Pronósticos
                  </Link>
                </>
              )}
            </>
          )}
        </Stack>
      </Box>
    </Box>
  );
};

export default Navbar; 